<template>
  <div class="ytcid-search">
    <div class="vx-col w-full mb-base">
      <vx-card class="w-full ytcid-search-card">
        <!-- Tabs row -->
        <div class="yt-tabs">
          <button class="yt-tab is-active" type="button">저작물</button>
          <button class="yt-tab is-disabled" type="button" disabled>출시</button>
          <button class="yt-tab is-disabled" type="button" disabled>아트 트랙</button>
        </div>

        <!-- Filter row -->
        <div class="yt-filter-row">
          <button class="yt-scope-pill" type="button">
            <span class="yt-scope-label">표시 중</span>
            <span class="yt-scope-value">내 저작물</span>
            <i class="el-icon-arrow-down"></i>
          </button>

          <div class="yt-search-input">
            <i class="el-icon-search yt-search-input__icon"></i>
            <input
              v-model="query"
              type="text"
              :placeholder="placeholder"
              @keyup.enter="run"
            />
            <button
              v-if="query"
              type="button"
              class="yt-search-input__clear"
              @click="clearQuery"
              aria-label="지우기"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>

          <div class="yt-type-chips">
            <span
              v-for="chip in chips"
              :key="chip.value || 'all'"
              class="yt-chip"
              :class="{ 'is-active': type === chip.value }"
              @click="setType(chip.value)"
            >{{ chip.label }}</span>
          </div>

          <div class="yt-page-size">
            <label class="yt-page-size__label">표시</label>
            <select v-model.number="pageSize" class="yt-page-size__select" @change="onPageSizeChange">
              <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">{{ opt }}건</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="yt-table-wrap">
          <table class="yt-table">
            <thead>
              <tr>
                <th class="yt-col-check"><span class="yt-cb-stub"></span></th>
                <th class="yt-col-asset">저작물</th>
                <th class="yt-col-ids">
                  <div>ISRC</div>
                  <div class="yt-col-ids__sub">맞춤 ID</div>
                </th>
                <th class="yt-col-type">유형</th>
                <th class="yt-col-date">생성일</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr><td colspan="5" class="yt-empty">불러오는 중…</td></tr>
              </template>
              <template v-else-if="rows.length">
                <tr
                  v-for="r in rows"
                  :key="r.id"
                  class="yt-row"
                  @click="open(r)"
                >
                  <td class="yt-col-check" @click.stop>
                    <span class="yt-cb-stub"></span>
                  </td>
                  <td class="yt-col-asset">
                    <div class="yt-asset-cell">
                      <span class="yt-asset-thumb"><i class="el-icon-headset"></i></span>
                      <div class="yt-asset-text">
                        <div class="yt-asset-title">{{ r.title || '제목 없음' }}</div>
                        <div v-if="r.artist" class="yt-asset-sub yt-asset-sub--artist">{{ r.artist }}</div>
                        <div v-if="r.label && r.label !== r.artist" class="yt-asset-sub yt-asset-sub--label">{{ r.label }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="yt-col-ids">
                    <div class="yt-mono">
                      {{ r.isrc || '—' }}<span v-if="r.isrcExtraCount" class="yt-isrc-extra"> (+{{ r.isrcExtraCount }})</span>
                    </div>
                    <div class="yt-mono yt-col-ids__sub">{{ r.customId || '—' }}</div>
                  </td>
                  <td class="yt-col-type">
                    <span v-if="r.type" class="yt-type-pill" :class="`yt-type-pill--${typeKey(r.type)}`">{{ formatType(r.type) }}</span>
                    <span v-else class="yt-type-label">—</span>
                  </td>
                  <td class="yt-col-date">
                    <div>{{ r.createdDate || '—' }}</div>
                    <div class="yt-time">{{ r.createdTime || '' }}</div>
                  </td>
                </tr>
              </template>
              <template v-else-if="searched">
                <tr><td colspan="5" class="yt-empty">결과 없음. 다른 검색어를 시도해보세요.</td></tr>
              </template>
              <template v-else>
                <tr><td colspan="5" class="yt-empty yt-empty--hint">
                  상단의 검색창에 저작물 ID, ISRC, 맞춤 ID 또는 동영상 ID를 입력하세요.
                </td></tr>
              </template>
            </tbody>
          </table>
        </div>

        <div v-if="searched && rows.length" class="yt-pager">
          <span class="yt-pager__count">
            {{ rows.length }}{{ totalResults != null ? ` / ${totalResults}` : '' }}건 표시
          </span>
          <el-button
            v-if="hasMore"
            size="mini"
            :loading="loadingMore"
            @click="loadMore"
          >더 불러오기</el-button>
        </div>

        <!-- Recents -->
        <div v-if="!searched && recents.length" class="yt-recents">
          <div class="yt-section-label">최근 검색</div>
          <div class="yt-recent-list">
            <span
              v-for="r in recents.slice(0, 8)"
              :key="r.term + r.at"
              class="yt-recent-chip"
              @click="quickJump(r)"
            >
              <i class="el-icon-time"></i>
              <span class="yt-mono">{{ r.term }}</span>
              <span v-if="r.title" class="yt-recent-chip__title">· {{ r.title }}</span>
            </span>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import { YoutubeCidApi, detectIdShape, loadRecent, pushRecent } from './api.js'
import { toLocalDate, toLocalTime } from './dateFormat.js'

const TYPE_CHIPS = [
  { label: '전체', value: '' },
  { label: '사운드 레코딩 (SR)', value: 'sound_recording' },
  { label: '아트 트랙 (AT)', value: 'art_track_video' },
  { label: '뮤직 비디오', value: 'music_video' },
  { label: '영화', value: 'movie' },
  { label: 'TV 에피소드', value: 'tv_episode' },
  { label: '웹', value: 'web' },
  { label: '음악작품 공유', value: 'composition' },
]

const TYPE_LABELS = {
  sound_recording: '사운드 레코딩 (SR)',
  art_track_video: '아트 트랙 (AT)',
  composition: '음악작품 공유',
  music_video: '뮤직 비디오',
  movie: '영화',
  episode: '에피소드',
  tv_episode: 'TV 에피소드',
  tv_season: 'TV 시즌',
  show: '프로그램',
  web: '웹',
  general: '일반',
  video_game: '비디오 게임',
}

function typeLabel(t) {
  if (!t) return ''
  return TYPE_LABELS[t] || String(t).replace(/_/g, ' ').toUpperCase()
}

function pickMd(item) {
  return item.metadataMine || item.metadata || item.metadataEffective || {}
}

function splitDateTime(iso) {
  if (!iso) return { date: '', time: '' }
  return { date: toLocalDate(iso), time: toLocalTime(iso) }
}

function formatPartialDate(v) {
  if (!v) return ''
  if (typeof v === 'object') {
    const { year, month, day } = v
    if (!year) return ''
    const m = month ? String(month).padStart(2, '0') : null
    const d = day ? String(day).padStart(2, '0') : null
    return [year, m, d].filter(Boolean).join('-')
  }
  return String(v).slice(0, 10)
}

export default {
  name: 'YoutubeCidAssetSearch',
  data() {
    return {
      query: '',
      type: '',
      results: [],
      recents: loadRecent(),
      loading: false,
      loadingMore: false,
      searched: false,
      chips: TYPE_CHIPS,
      pageSize: 30,
      pageSizeOptions: [10, 30, 50],
      nextPageToken: '',
      totalResults: null,
      lastSearchParams: null,
    }
  },
  computed: {
    placeholder() {
      return '저작물 ID, 맞춤 ID, ISRC 또는 동영상 ID로 필터링'
    },
    hasMore() {
      return !!this.nextPageToken
    },
    rows() {
      return this.results.map((item) => {
        const md = pickMd(item)
        const created = splitDateTime(item.timeCreated || md.timeCreated)
        const updated = splitDateTime(item.timeUpdated || md.timeUpdated)
        const isrcs = Array.isArray(item.isrcs) ? item.isrcs : (md.isrcs || [])
        const primaryIsrc = item.isrc || md.isrc || isrcs[0] || ''
        const extraIsrcs = Math.max(0, isrcs.length - (primaryIsrc ? 1 : 0))
        const rs = item.nWayRevenueSharing || {}
        const rsStatus = String(rs.status || '').toLowerCase()
        const lic = item.licensability || {}
        const licReason = Array.isArray(lic.unlicensableReason) && lic.unlicensableReason.length
          ? lic.unlicensableReason.join(', ').replace(/_/g, ' ')
          : (lic.licensabilityState || '').replace(/_/g, ' ')
        return {
          id: item.id,
          type: item.type,
          title: item.title || md.title || md.episodeTitle || md.movieTitle || md.songTitle || '',
          artist: item.artist || (Array.isArray(md.artist) ? md.artist.filter(Boolean).join(', ') : md.artist) || '',
          label: md.label || md.studio || '',
          isrc: primaryIsrc,
          isrcExtraCount: extraIsrcs,
          customId: item.customId || md.customId || '',
          createdDate: created.date,
          createdTime: created.time,
          updatedDate: updated.date,
          updatedTime: updated.time,
          releaseDate: formatPartialDate(md.releaseDate || item.releaseDate),
          revenueSharing: rsStatus ? {
            ok: rsStatus.includes('unrestricted'),
            label: rsStatus.replace(/_/g, ' '),
          } : null,
          licensable: typeof lic.licensable === 'boolean' ? {
            ok: lic.licensable,
            reason: lic.licensable ? '라이선스 부여 가능' : (licReason || '라이선스 부여 불가'),
          } : null,
        }
      })
    },
  },
  methods: {
    clearQuery() {
      this.query = ''
      this.results = []
      this.searched = false
      this.nextPageToken = ''
      this.totalResults = null
      this.lastSearchParams = null
    },
    setType(value) {
      this.type = value
      if (this.query) this.run()
    },
    onPageSizeChange() {
      if (this.searched && this.query) this.run()
    },
    formatType(t) {
      return typeLabel(t)
    },
    typeKey(t) {
      return String(t || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    },
    quickJump(r) {
      this.query = r.term
      this.run()
    },
    open(item) {
      pushRecent({ term: item.id, shape: 'asset', title: item.title })
      this.$router.push(`/youtube-cid/asset/${item.id}`)
    },
    async run() {
      const q = (this.query || '').trim()
      if (!q) {
        this.results = []
        this.searched = false
        this.nextPageToken = ''
        this.totalResults = null
        this.lastSearchParams = null
        return
      }
      const shape = detectIdShape(q)

      if (shape === 'asset' && !this.type) {
        pushRecent({ term: q, shape: 'asset' })
        this.$router.push(`/youtube-cid/asset/${q}`)
        return
      }

      if (shape === 'video' && !this.type) {
        pushRecent({ term: q, shape: 'video' })
        this.$router.push(`/youtube-cid/video/${q}`)
        return
      }

      this.loading = true
      this.searched = true
      try {
        const params = { q, maxResults: String(this.pageSize) }
        if (this.type) params.type = this.type
        if (shape === 'isrc') params.isrc = q
        if (shape === 'video') params.videoId = q
        this.lastSearchParams = { ...params }
        const data = await YoutubeCidApi.searchAssets(params)
        this.results = (data && data.items) || []
        this.nextPageToken = (data && data.nextPageToken) || ''
        const total = data && data.pageInfo && Number(data.pageInfo.totalResults)
        this.totalResults = Number.isFinite(total) ? total : null
        if (this.results.length === 1 && !this.nextPageToken) {
          const only = this.results[0]
          const md = pickMd(only)
          pushRecent({ term: only.id, shape: 'asset', title: md.title || '' })
          this.$router.push(`/youtube-cid/asset/${only.id}`)
        } else {
          pushRecent({ term: q, shape: shape || 'custom' })
          this.recents = loadRecent()
        }
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (!this.nextPageToken || this.loadingMore || !this.lastSearchParams) return
      this.loadingMore = true
      try {
        const params = { ...this.lastSearchParams, pageToken: this.nextPageToken }
        const data = await YoutubeCidApi.searchAssets(params)
        const more = (data && data.items) || []
        this.results = [...this.results, ...more]
        this.nextPageToken = (data && data.nextPageToken) || ''
        const total = data && data.pageInfo && Number(data.pageInfo.totalResults)
        if (Number.isFinite(total)) this.totalResults = total
      } finally {
        this.loadingMore = false
      }
    },
  },
  created() {
    const q = this.$route.query.q
    const type = this.$route.query.type
    if (typeof type === 'string' && type) this.type = type
    if (typeof q === 'string' && q.trim()) {
      this.query = q.trim()
      this.run()
    }
  },
}
</script>

<style lang="scss" scoped>
$yt-border: rgba(0, 0, 0, 0.08);
$yt-border-strong: rgba(0, 0, 0, 0.14);
$yt-text: #0f0f0f;
$yt-text-muted: rgba(15, 15, 15, 0.6);
$yt-text-faint: rgba(15, 15, 15, 0.42);
$yt-bg-hover: rgba(15, 15, 15, 0.04);
$yt-accent: #065fd4;

.ytcid-search {
  font-family: 'Roboto', 'YouTube Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: $yt-text;
}
.ytcid-search-card ::v-deep .vx-card__body {
  padding: 0;
}

.yt-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
  border-bottom: 1px solid $yt-border;
}
.yt-tab {
  background: transparent;
  border: 0;
  height: 48px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.2px;
  color: $yt-text-muted;
  cursor: pointer;
  position: relative;
  &:hover:not(.is-disabled) { color: $yt-text; }
  &.is-active {
    color: $yt-text;
    font-weight: 600;
    &::after {
      content: '';
      position: absolute;
      left: 12px; right: 12px; bottom: -1px;
      height: 2px;
      background: $yt-text;
      border-radius: 2px 2px 0 0;
    }
  }
  &.is-disabled {
    color: $yt-text-faint;
    cursor: not-allowed;
  }
}

.yt-filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid $yt-border;
  flex-wrap: wrap;
}
.yt-scope-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid $yt-border-strong;
  border-radius: 16px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  color: $yt-text;
  &:hover { background: $yt-bg-hover; }
  i { font-size: 12px; opacity: 0.6; }
}
.yt-scope-label { color: $yt-text-muted; }
.yt-scope-value { font-weight: 500; }

.yt-search-input {
  flex: 1;
  min-width: 240px;
  position: relative;
  display: flex;
  align-items: center;
  height: 36px;
  border: 1px solid $yt-border-strong;
  border-radius: 4px;
  background: #fff;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &:focus-within {
    border-color: $yt-accent;
    box-shadow: inset 0 0 0 1px $yt-accent;
  }
}
.yt-search-input__icon {
  position: absolute;
  left: 10px;
  color: $yt-text-faint;
  font-size: 14px;
  pointer-events: none;
}
.yt-search-input input {
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 13px;
  color: $yt-text;
  padding: 0 32px 0 32px;
  height: 100%;
  &::placeholder { color: $yt-text-faint; }
}
.yt-search-input__clear {
  position: absolute;
  right: 6px;
  background: transparent;
  border: 0;
  color: $yt-text-faint;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: inline-flex;
  &:hover { background: $yt-bg-hover; color: $yt-text; }
}

.yt-type-chips {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.yt-chip {
  display: inline-block;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid $yt-border;
  color: $yt-text-muted;
  background: #fff;
  transition: background 100ms ease, color 100ms ease, border-color 100ms ease;
  &:hover { background: $yt-bg-hover; color: $yt-text; }
  &.is-active {
    background: rgba(6, 95, 212, 0.08);
    border-color: rgba(6, 95, 212, 0.4);
    color: $yt-accent;
    font-weight: 500;
  }
}

.yt-table-wrap {
  overflow-x: auto;
}
.yt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: $yt-text;
  thead th {
    text-align: left;
    font-weight: 500;
    color: $yt-text-muted;
    padding: 10px 12px;
    border-bottom: 1px solid $yt-border;
    background: #fff;
    white-space: nowrap;
    vertical-align: bottom;
  }
  tbody td {
    padding: 12px;
    border-bottom: 1px solid $yt-border;
    vertical-align: top;
    white-space: nowrap;
  }
  .yt-col-num { text-align: right; }
  .yt-col-icon { text-align: center; width: 64px; color: $yt-text-muted; }
  .yt-col-check { width: 36px; padding-right: 0; }
  .yt-col-asset { min-width: 280px; white-space: normal; }
  .yt-col-ids__sub { color: $yt-text-faint; font-size: 12px; margin-top: 2px; }
  tbody td.yt-col-type { vertical-align: middle; }
}

.yt-row {
  cursor: pointer;
  transition: background 80ms ease;
  &:hover { background: $yt-bg-hover; }
}
.yt-cb-stub {
  display: inline-block;
  width: 16px; height: 16px;
  border: 1.5px solid rgba(15,15,15,0.3);
  border-radius: 2px;
  background: #fff;
}

.yt-asset-cell {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.yt-asset-thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px; height: 40px;
  border-radius: 4px;
  background: rgba(6, 95, 212, 0.08);
  color: $yt-accent;
  font-size: 18px;
  flex-shrink: 0;
}
.yt-asset-text { min-width: 0; }
.yt-asset-title {
  font-size: 14px;
  color: $yt-text;
  font-weight: 500;
  line-height: 1.3;
  word-break: break-word;
}
.yt-asset-sub {
  font-size: 12px;
  color: $yt-text-faint;
  margin-top: 2px;
}
.yt-asset-sub--label { color: $yt-text-muted; }
.yt-type-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: $yt-text-muted;
}
.yt-type-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
  line-height: 1.4;
  border: 1px solid transparent;
  background: rgba(15, 15, 15, 0.06);
  color: $yt-text-muted;
  &--sound-recording {
    background: rgba(6, 95, 212, 0.1);
    color: #065fd4;
    border-color: rgba(6, 95, 212, 0.25);
  }
  &--art-track-video {
    background: rgba(142, 36, 170, 0.1);
    color: #8e24aa;
    border-color: rgba(142, 36, 170, 0.25);
  }
  &--music-video {
    background: rgba(216, 27, 96, 0.1);
    color: #d81b60;
    border-color: rgba(216, 27, 96, 0.25);
  }
  &--composition {
    background: rgba(57, 73, 171, 0.1);
    color: #3949ab;
    border-color: rgba(57, 73, 171, 0.25);
  }
  &--movie {
    background: rgba(230, 81, 0, 0.1);
    color: #e65100;
    border-color: rgba(230, 81, 0, 0.25);
  }
  &--tv-episode,
  &--episode,
  &--tv-season,
  &--show {
    background: rgba(46, 125, 50, 0.1);
    color: #2e7d32;
    border-color: rgba(46, 125, 50, 0.25);
  }
  &--web {
    background: rgba(0, 131, 143, 0.1);
    color: #00838f;
    border-color: rgba(0, 131, 143, 0.25);
  }
  &--video-game {
    background: rgba(255, 143, 0, 0.12);
    color: #ef6c00;
    border-color: rgba(255, 143, 0, 0.3);
  }
  &--general {
    background: rgba(97, 97, 97, 0.1);
    color: #616161;
    border-color: rgba(97, 97, 97, 0.25);
  }
}

.yt-mono {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
.yt-isrc-extra { color: $yt-text-faint; font-weight: 400; }
.yt-muted { color: $yt-text-faint; }
.yt-time { font-size: 11px; color: $yt-text-faint; margin-top: 2px; }

.yt-icon-ok { color: #2e7d32; font-size: 16px; }
.yt-icon-warn { color: #ed6c02; font-size: 16px; }

.yt-empty {
  padding: 48px 16px;
  text-align: center;
  color: $yt-text-faint;
  font-size: 13px;
  border-bottom: 0 !important;
  &--hint { color: $yt-text-muted; }
}

.yt-page-size {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}
.yt-page-size__label {
  font-size: 12px;
  color: $yt-text-muted;
}
.yt-page-size__select {
  height: 28px;
  font-size: 12px;
  padding: 0 8px;
  border: 1px solid $yt-border-strong;
  border-radius: 4px;
  background: #fff;
  color: $yt-text;
  cursor: pointer;
  &:focus { outline: none; border-color: $yt-accent; }
}

.yt-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid $yt-border;
  font-size: 12px;
  color: $yt-text-muted;
}
.yt-pager__count { font-size: 12px; }

.yt-recents {
  padding: 16px;
  border-top: 1px solid $yt-border;
}
.yt-section-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $yt-text-faint;
  margin-bottom: 10px;
}
.yt-recent-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.yt-recent-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  border: 1px solid $yt-border;
  font-size: 12px;
  cursor: pointer;
  background: #fff;
  &:hover { background: $yt-bg-hover; border-color: $yt-border-strong; }
  i { font-size: 12px; opacity: 0.5; }
}
.yt-recent-chip__title { color: $yt-text-muted; }
</style>
